import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useProjectsList } from "@/hooks";
import ProjectCard from "./components/ProjectCard";
import ProjectTable from "./components/ProjectTable";
import ProjectFilters from "./components/ProjectFilters";
import PageHeader from "@/components/shared/PageHeader";
import KpiCard from "@/components/shared/KpiCard";
import EmptyState from "@/components/shared/EmptyState";

export default function Projetos() {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<"cards" | "table">("cards");
  const [filters, setFilters] = useState<Record<string, string>>({});
  const { items: projetos, loading } = useProjectsList();

  const filtered = useMemo(() => {
    return projetos.filter((p) => {
      if (filters.search && !p.nome.toLowerCase().includes(filters.search)) return false;
      if (filters.municipio && !p.municipio.toLowerCase().includes(filters.municipio)) return false;
      if (filters.uf && p.uf !== filters.uf) return false;
      if (filters.tipo && p.tipo !== filters.tipo) return false;
      if (filters.status && p.status !== filters.status) return false;
      return true;
    });
  }, [filters, projetos]);

  const kpis = useMemo(() => {
    const ativos = projetos.filter((p) =>
      !["concluido", "homologado", "arquivado", "cancelado"].includes(p.status)
    ).length;
    const emCampo = projetos.filter((p) => p.status === "em campo" || p.status === "em_campo").length;
    const emRevisao = projetos.filter((p) => p.status === "em revisão" || p.status === "em_revisao").length;
    const concluidos = projetos.filter((p) => p.status === "concluido" || p.status === "homologado").length;
    const totalArvores = projetos.reduce((sum, p) => sum + p.totalArvores, 0);
    const totalVistorias = projetos.reduce((sum, p) => sum + p.vistoriasRealizadas, 0);
    return { ativos, emCampo, emRevisao, concluidos, totalArvores, totalVistorias };
  }, [projetos]);

  const projetosAtrasados = projetos.filter((p) => {
    if (["concluido", "homologado", "arquivado"].includes(p.status)) return false;
    if (!p.dataPrevisao) return false;
    const hoje = new Date();
    const previsao = new Date(p.dataPrevisao);
    return previsao < hoje && p.progresso < 100;
  });

  const projetosCriticos = projetos.filter((p) => p.riscoCritico > 0);
  const projetosBaixaEvolucao = projetos.filter((p) =>
    !["concluido", "homologado", "arquivado"].includes(p.status) && p.progresso < 40 && p.progresso > 0
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Projetos de Inventário"
        subtitle="Gerencie campanhas de implantação do CTA, inventários urbanos, áreas verdes, UCs e monitoramentos ambientais."
      >
        <button onClick={() => navigate("/projetos/novo")} className="btn-primary text-xs px-3 py-2 cursor-pointer">
          <i className="ri-add-line"></i>
          <span className="hidden sm:inline">Novo Projeto</span>
        </button>
      </PageHeader>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
        <KpiCard label="Projetos Ativos" value={kpis.ativos} icon="ri-folder-chart-line" variant="success" />
        <KpiCard label="Em Campo" value={kpis.emCampo} icon="ri-map-pin-line" variant="info" />
        <KpiCard label="Em Revisão" value={kpis.emRevisao} icon="ri-edit-line" variant="warning" />
        <KpiCard label="Concluídos" value={kpis.concluidos} icon="ri-check-double-line" variant="info" />
        <KpiCard label="Exemplares cadastrados" value={kpis.totalArvores.toLocaleString("pt-BR")} icon="ri-plant-line" variant="success" />
        <KpiCard label="Vistorias Realizadas" value={kpis.totalVistorias.toLocaleString("pt-BR")} icon="ri-survey-line" variant="default" />
      </div>

      {/* Filters */}
      <ProjectFilters projetos={projetos} onFilter={setFilters} />

      {/* View Toggle + Results */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-foreground-500">
          {loading ? "Carregando..." : (
            <>
              {filtered.length} {filtered.length === 1 ? "projeto" : "projetos"} encontrado{filtered.length !== 1 ? "s" : ""}
              {Object.values(filters).some((v) => !!v) && " (com filtros aplicados)"}
            </>
          )}
        </p>
        <div className="flex items-center bg-white rounded-lg border border-background-200/70 p-0.5">
          <button
            onClick={() => setViewMode("cards")}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors cursor-pointer ${
              viewMode === "cards" ? "bg-primary-50 text-primary-700" : "text-foreground-500 hover:text-foreground-700"
            }`}
          >
            <i className="ri-grid-line mr-1"></i>Cards
          </button>
          <button
            onClick={() => setViewMode("table")}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors cursor-pointer ${
              viewMode === "table" ? "bg-primary-50 text-primary-700" : "text-foreground-500 hover:text-foreground-700"
            }`}
          >
            <i className="ri-table-line mr-1"></i>Tabela
          </button>
        </div>
      </div>

      {/* Projects List */}
      {filtered.length > 0 ? (
        viewMode === "cards" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map((projeto) => (
              <ProjectCard key={projeto.id} projeto={projeto} />
            ))}
          </div>
        ) : (
          <ProjectTable projetos={filtered} />
        )
      ) : (
        <EmptyState
          icon={loading ? "ri-loader-4-line" : "ri-search-line"}
          title={loading ? "Carregando projetos..." : "Nenhum projeto encontrado"}
          description={loading ? "Buscando dados do servidor." : "Ajuste os filtros para ver mais resultados."}
        />
      )}

      {/* Projetos Prioritários */}
      {(projetosAtrasados.length > 0 || projetosCriticos.length > 0 || projetosBaixaEvolucao.length > 0) && (
        <div className="bg-white rounded-card border border-background-200/70 p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-warning-100 flex items-center justify-center">
              <i className="ri-flag-line text-warning-600 text-sm"></i>
            </div>
            <h3 className="text-sm font-semibold text-foreground-900 font-heading">Projetos Prioritários</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {projetosAtrasados.length > 0 && (
              <div className="p-3 rounded-lg border border-danger-200 bg-danger-50/50">
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-2 h-2 rounded-full bg-danger-500"></span>
                  <span className="text-xs font-medium text-danger-700">Atrasados</span>
                </div>
                <p className="text-lg font-bold text-foreground-900">{projetosAtrasados.length}</p>
                <p className="text-xs text-foreground-500">Prazo excedido</p>
              </div>
            )}
            {projetosCriticos.length > 0 && (
              <div className="p-3 rounded-lg border border-danger-200 bg-danger-50/50">
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-2 h-2 rounded-full bg-danger-500"></span>
                  <span className="text-xs font-medium text-danger-700">Exemplares em risco</span>
                </div>
                <p className="text-lg font-bold text-foreground-900">{projetosCriticos.length}</p>
                <p className="text-xs text-foreground-500">Com risco alto/crítico</p>
              </div>
            )}
            {projetosBaixaEvolucao.length > 0 && (
              <div className="p-3 rounded-lg border border-caution-200 bg-caution-50/50">
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-2 h-2 rounded-full bg-caution-500"></span>
                  <span className="text-xs font-medium text-caution-700">Baixa Evolução</span>
                </div>
                <p className="text-lg font-bold text-foreground-900">{projetosBaixaEvolucao.length}</p>
                <p className="text-xs text-foreground-500">Progresso &lt;40%</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
